// The group's progressive royal flush pot, as a card at the top of its page.
//
// Everyone sees the amount and who has won it. The holder or an admin changes the
// amount and the holder; ANYONE can announce a win (Will, 2026-10-17: the holder
// may be on vacation and the group is honest), and the audit trail is what makes
// that safe. The server decides all of it, this file only shows and asks.

import {
  ApiClientError,
  fetchPot,
  listMembers,
  payRoyalFlushPot,
  setPotAmount,
  setPotHolder,
} from '../api/client.js';
import { el } from '../dom.js';
import { fmtInstant, TimeFormat, type GroupMember, type GroupPot, type PotLogEntry } from '@msc/shared';

const dollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/** "120", "120.5" or "$1,200" to cents; null when it is not a money amount. */
function parseCents(raw: string): number | null {
  const t = raw.replace(/[$,\s]/g, '');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function groupPotCard(initial: GroupPot, groupId: number, zone: string): HTMLElement {
  let pot = initial;
  const root = el('section', { class: 'card group-pot' });
  let roster: GroupMember[] | null = null;

  async function members(): Promise<GroupMember[]> {
    roster ??= await listMembers(groupId);
    return roster;
  }
  const nameOf = (m: GroupMember): string => m.fullName || m.email || `User ${m.userId}`;

  function adopt(next: GroupPot): void {
    pot = next;
    render();
  }

  function errorText(err: unknown): string {
    return err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
  }

  function line(entry: PotLogEntry): string {
    const when = fmtInstant(entry.createdAt, zone, TimeFormat.INSTANT);
    if (entry.kind === 'win') {
      return `${when}: ${entry.subjectName} won ${dollars(entry.wonCents ?? 0)} (announced by ${entry.actorName}), pot restarted at ${dollars(entry.newCents ?? 0)}`;
    }
    if (entry.kind === 'holder') {
      return `${when}: ${entry.actorName} made ${entry.subjectName ?? 'nobody'} the holder`;
    }
    return `${when}: ${entry.actorName} changed the pot from ${dollars(entry.oldCents ?? 0)} to ${dollars(entry.newCents ?? 0)}`;
  }

  function editAmount(): HTMLElement {
    const input = el('input', {
      type: 'text',
      inputmode: 'decimal',
      value: (pot.amountCents / 100).toFixed(2),
      'aria-label': 'Pot amount in dollars',
      autocomplete: 'off',
    }) as HTMLInputElement;
    const err = el('p', { class: 'form-error', hidden: true });
    const save = el('button', { type: 'button', class: 'btn' }, 'Update amount') as HTMLButtonElement;
    save.addEventListener('click', async () => {
      const cents = parseCents(input.value);
      if (cents === null) {
        err.textContent = 'Enter the amount in dollars, like 340 or 340.50.';
        err.hidden = false;
        return;
      }
      err.hidden = true;
      save.setAttribute('disabled', 'disabled');
      try {
        adopt(await setPotAmount(groupId, cents));
      } catch (e) {
        err.textContent = errorText(e);
        err.hidden = false;
        save.removeAttribute('disabled');
      }
    });
    return el('div', { class: 'pot-edit' }, [
      el('label', {}, 'Pot amount ($)'),
      el('div', { class: 'pot-edit-row' }, [input, save]),
      err,
    ]);
  }

  function holderPicker(): HTMLElement {
    const box = el('div', { class: 'pot-holder' });
    const open = el('button', { type: 'button', class: 'btn btn-link' }, 'Change holder') as HTMLButtonElement;
    open.addEventListener('click', async () => {
      open.setAttribute('disabled', 'disabled');
      try {
        const list = await members();
        const pick = el('select', { 'aria-label': 'New holder' }) as HTMLSelectElement;
        pick.append(el('option', { value: '0' }, 'Nobody'));
        for (const m of list) pick.append(el('option', { value: String(m.userId) }, nameOf(m)));
        pick.value = String(pot.holder?.userId ?? 0);
        const go = el('button', { type: 'button', class: 'btn' }, 'Save holder') as HTMLButtonElement;
        const err = el('p', { class: 'form-error', hidden: true });
        go.addEventListener('click', async () => {
          go.setAttribute('disabled', 'disabled');
          try {
            adopt(await setPotHolder(groupId, Number(pick.value) || null));
          } catch (e) {
            err.textContent = errorText(e);
            err.hidden = false;
            go.removeAttribute('disabled');
          }
        });
        box.replaceChildren(el('div', { class: 'pot-edit-row' }, [pick, go]), err);
      } catch (e) {
        open.removeAttribute('disabled');
        window.alert(errorText(e));
      }
    });
    box.append(open);
    return box;
  }

  /** Anyone: pick the winner, confirm the figure, announce. */
  function announce(): HTMLElement {
    const box = el('details', { class: 'pot-announce' });
    box.append(el('summary', {}, 'Someone hit a royal flush'));
    const body = el('div', { class: 'pot-announce-body' });
    box.append(body);

    box.addEventListener('toggle', async () => {
      if (!(box as HTMLDetailsElement).open || body.childElementCount > 0) return;
      body.append(el('p', { class: 'muted small' }, 'Loading members'));
      try {
        const list = await members();
        const pick = el('select', { id: 'pot-winner' }) as HTMLSelectElement;
        for (const m of list) pick.append(el('option', { value: String(m.userId) }, nameOf(m)));
        const restart = el('input', {
          type: 'text',
          inputmode: 'decimal',
          value: '0.00',
          autocomplete: 'off',
          id: 'pot-restart',
        }) as HTMLInputElement;
        const err = el('p', { class: 'form-error', hidden: true });
        const go = el('button', { type: 'button', class: 'btn' }, 'Announce to the group') as HTMLButtonElement;
        go.addEventListener('click', async () => {
          const winner = Number(pick.value);
          const restartCents = parseCents(restart.value);
          if (!winner || restartCents === null) {
            err.textContent = 'Pick the winner and enter where the pot restarts, in dollars.';
            err.hidden = false;
            return;
          }
          const who = pick.selectedOptions[0]?.textContent ?? 'the winner';
          if (
            !window.confirm(
              `Announce that ${who} won ${dollars(pot.amountCents)}? The group will be told and the pot restarts at ${dollars(restartCents)}.`,
            )
          ) {
            return;
          }
          err.hidden = true;
          go.setAttribute('disabled', 'disabled');
          try {
            const res = await payRoyalFlushPot(groupId, {
              winnerUserId: winner,
              amountCents: pot.amountCents,
              restartCents,
            });
            adopt(res.pot);
          } catch (e) {
            err.textContent = errorText(e);
            err.hidden = false;
            go.removeAttribute('disabled');
            // A 409 means the amount changed under us. Show the real one.
            if (e instanceof ApiClientError && e.status === 409) {
              void fetchPot(groupId).then(adopt, () => undefined);
            }
          }
        });
        body.replaceChildren(
          el('label', { for: 'pot-winner' }, 'Who won'),
          pick,
          el('label', { for: 'pot-restart' }, 'Pot restarts at ($)'),
          restart,
          err,
          go,
        );
      } catch (e) {
        body.replaceChildren(el('p', { class: 'form-error' }, errorText(e)));
      }
    });
    return box;
  }

  function render(): void {
    const wins = pot.log.filter((l) => l.kind === 'win');
    const holder = pot.holder ? pot.holder.displayName : 'nobody yet';
    const updated =
      pot.updatedAt && pot.updatedByName
        ? ` · updated ${fmtInstant(pot.updatedAt, zone, TimeFormat.INSTANT)} by ${pot.updatedByName}`
        : '';

    root.replaceChildren(
      el('h2', {}, '👑 Royal flush pot'),
      el('p', { class: 'pot-amount' }, dollars(pot.amountCents)),
      el('p', { class: 'muted small' }, `Held by ${holder}${updated}`),
      ...(pot.canEdit ? [editAmount()] : []),
      ...(pot.canSetHolder ? [holderPicker()] : []),
      announce(),
      el('h3', {}, 'Who has won it'),
      wins.length === 0
        ? el('p', { class: 'muted small' }, 'Nobody yet.')
        : el(
            'ul',
            { class: 'pot-wins' },
            wins.map((w) => el('li', {}, line(w))),
          ),
      el('details', { class: 'pot-log' }, [
        el('summary', {}, 'Change log'),
        pot.log.length === 0
          ? el('p', { class: 'muted small' }, 'No changes yet.')
          : el(
              'ul',
              { class: 'small' },
              pot.log.map((l) => el('li', {}, line(l))),
            ),
      ]),
    );
  }

  render();
  return root;
}
